import React, { useCallback, useState } from 'react'
import Tippy from '@tippyjs/react'

import Icon from '../ui/Icon'

interface NumberInputProps {
  id?: string
  name: string
  value: string
  required?: boolean
  label?: string
  placeHolder?: string
  className?: string
  disabled?: boolean
  min?: number
  max?: number
  step?: number | 'any'
  infoMessage?: string
  errorMessage?: string
  onChange: (name: string, value: string, isValid: boolean) => void
  onValidate?: (value: string) => boolean
}

export const NumberInput = ({
  id,
  name,
  value,
  required = false,
  label,
  placeHolder = '',
  className = '',
  disabled = false,
  min,
  max,
  step,
  infoMessage,
  errorMessage,
  onChange,
  onValidate,
}: NumberInputProps) => {
  const [isError, setIsError] = useState(false)
  const [isTouch, setIsTouch] = useState(false)
  const [isFocus, setIsFocus] = useState(false)

  const isRequired = isTouch && required && !value.trim()

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setIsTouch(true)

      const currentValue = event.target.value
      const hasValue = currentValue.trim().length > 0
      const nativeValid = event.target.validity.valid
      const customValid = onValidate ? onValidate(currentValue) : true
      const valid = hasValue && nativeValid && customValid

      setIsError(hasValue && !valid)
      onChange(name, currentValue, valid)
    },
    [name, onChange, onValidate]
  )

  const fieldClass = [
    'field',
    className,
    required && 'required',
    !isFocus && isRequired && 'is-required',
    !isFocus && isError && 'is-error',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={fieldClass}>
      {label && <label htmlFor={id}>{label}</label>}

      <div className="input-icon-wrap">
        <input
          id={id}
          className="input"
          type="number"
          placeholder={disabled ? '' : placeHolder}
          required={required}
          disabled={disabled}
          min={min}
          max={max}
          step={step}
          name={name}
          value={value}
          onChange={handleChange}
          onFocus={() => setIsFocus(true)}
          onBlur={() => setIsFocus(false)}
        />

        {infoMessage && (
          <Tippy content={infoMessage} placement="top" className="ph-tooltip">
            <div className="input-icon-toggle">
              <Icon name="info" size={16} />
            </div>
          </Tippy>
        )}
      </div>

      {errorMessage && !isFocus && isError && (
        <div className="field-msg err" role="alert">
          <span className="ico">
            <Icon name="warn" size={13} />
          </span>

          {errorMessage}
        </div>
      )}
    </div>
  )
}
